/**
 * BaseGlydeClient - Shared Base Class for GLYDE SDK Clients
 *
 * Provides common functionality for both voice and text chat clients:
 * - Authentication header management (publishableKey, apiKey, authToken)
 * - Unity API base URL configuration
 * - Event emission utilities
 * - Context management
 *
 * @packageDocumentation
 */

import type { BaseGlydeConfig, VoiceContextType } from './types';

/**
 * Generic event type for clients to extend
 */
export interface BaseEvent {
  type: string;
  payload?: unknown;
}

/**
 * Context information for client operations
 */
export interface ClientContext {
  contextType?: VoiceContextType | string;
  contextId?: string;
  clientUuid?: string;
  candidateUuid?: string;
  jobUuid?: string;
}

/**
 * Authentication method used by the client
 */
export type AuthMethod = 'publishable_key' | 'api_key' | 'auth_token' | 'none';

/**
 * Abstract base class for GLYDE SDK clients
 *
 * @template TConfig - Configuration type extending BaseGlydeConfig
 * @template TEvent - Event type for this client
 *
 * @example
 * ```typescript
 * class MyClient extends BaseGlydeClient<MyConfig, MyEvent> {
 *   protected emitEvent(event: MyEvent): void {
 *     if (this.config.onEvent) this.config.onEvent(event);
 *   }
 * }
 * ```
 */
export abstract class BaseGlydeClient<TConfig extends BaseGlydeConfig, TEvent extends BaseEvent> {
  protected config: TConfig;
  protected unityUrl: string;
  protected context: ClientContext = {};
  protected initialized = false;

  /**
   * Create a new BaseGlydeClient instance
   * @param config - Configuration options
   */
  constructor(config: TConfig) {
    this.config = config;
    this.unityUrl = config.unityBaseUrl || 'https://api.glydeunity.com';

    // Initialize context from config
    this.context.contextId = config.contextId;

    // Validate authentication
    if (!config.publishableKey && !config.apiKey && !config.authToken) {
      console.warn('[BaseGlydeClient] No authentication method provided. One of publishableKey, apiKey, or authToken is required.');
    }
  }

  /**
   * Determine the authentication method being used
   * @returns The auth method type
   */
  protected getAuthMethod(): AuthMethod {
    if (this.config.publishableKey) return 'publishable_key';
    if (this.config.apiKey) return 'api_key';
    if (this.config.authToken) return 'auth_token';
    return 'none';
  }

  /**
   * Get authentication headers based on configured auth method
   * @returns Headers object with appropriate authentication
   */
  protected getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (this.config.publishableKey) {
      headers['x-publishable-key'] = this.config.publishableKey;
    }

    if (this.config.apiKey) {
      headers['x-api-key'] = this.config.apiKey;
    }

    if (this.config.authToken) {
      headers['Authorization'] = `Bearer ${this.config.authToken}`;
    }

    return headers;
  }

  /**
   * Make an authenticated request to the Unity API
   * @param path - API endpoint path (e.g., '/api/unity/voice/auth')
   * @param options - Fetch options
   * @returns Response data
   */
  protected async unityFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.unityUrl}${path}`;
    const headers = {
      ...this.getAuthHeaders(),
      ...(options.headers || {})
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error?.message || errorData.message || `Request failed: ${response.status}`;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Emit an event to the configured callback
   * @param event - Event to emit
   */
  protected abstract emitEvent(event: TEvent): void;

  /**
   * Update the current context
   * @param context - Partial context to merge
   */
  protected updateContext(context: Partial<ClientContext>): void {
    this.context = { ...this.context, ...context };
  }

  /**
   * Get the current context
   * @returns Current client context
   */
  public getContext(): ClientContext {
    return { ...this.context };
  }

  /**
   * Get the configured Unity API base URL
   * @returns Unity API base URL
   */
  public getUnityUrl(): string {
    return this.unityUrl;
  }

  /**
   * Check if the client has been initialized
   * @returns True if initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get the authentication method being used
   * @returns Authentication method type
   */
  public getAuthMethodType(): AuthMethod {
    return this.getAuthMethod();
  }
}
